import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Context, { AccessContext } from '../components/context.js';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, rect } from 'recharts';
import OrderPill from '../components/order-pill.jsx';
import List from '../components/list.js';

const ArrowIcon = ({ up }) => up ? 
(
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3.33398 8.00065L8.00065 3.33398L12.6673 8.00065" stroke="#00C950" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8 12.6673V3.33398" stroke="#00C950" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 3.33398V12.6673" stroke="#99A1AF" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.6673 8L8.00065 12.6667L3.33398 8" stroke="#99A1AF" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
);
const PercentChange = ({ newValue, oldValue }) => {
    if (oldValue === 0) {
        return '';
    }
    const change = ((newValue - oldValue) / oldValue) * 100;
    const isPositive = change > 0;
    const formattedChange = Math.abs(change).toFixed(0) + '%';
    
    return `${formattedChange} ${isPositive ? 'more' : 'less'} than usual`;
}
const EpisodeAirDate = ({ airDate }) => {
    if (!airDate) {
        return (
            <span className="episode-air-date">
                N/A
            </span>
        );
    }
    const airDateObj = new Date(airDate);
    const now = new Date();
    const isPast = airDateObj < now;
    const options = { month: 'numeric', day: 'numeric', year: 'numeric' };
    return (
        <span className="episode-air-date">
            {airDateObj.toLocaleDateString(undefined, options)}
        </span>
    );
};
const EpisodeTitle = ({ title, imageUrl }) => {
    return (
        <div className="episode-title-container">
            <img src={imageUrl.startsWith('http') ? imageUrl : process.env.REACT_APP_MEDIA_URL + imageUrl} alt={title} />
            <span className="episode-title">
                {title}
            </span>
        </div>
    );
};
const EpisodeDuration = ({ duration }) => {
    const minutes = Math.floor(duration / 60);
    const hours = Math.floor(minutes / 60);
    const displayMinutes = minutes % 60;
    const seconds = duration % 60;
    return (
        <span className="episode-duration">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <g clip-path="url(#clip0_301_112)">
                    <path d="M6.99935 12.8327C10.221 12.8327 12.8327 10.221 12.8327 6.99935C12.8327 3.77769 10.221 1.16602 6.99935 1.16602C3.77769 1.16602 1.16602 3.77769 1.16602 6.99935C1.16602 10.221 3.77769 12.8327 6.99935 12.8327Z" stroke="#4A5565" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M7 3.5V7L9.33333 8.16667" stroke="#4A5565" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                    <clipPath id="clip0_301_112">
                        <rect width="14" height="14" fill="white"/>
                    </clipPath>
                </defs>
            </svg>
            <span>{hours > 0 ? `${hours}:` : ''}{hours > 0 ? displayMinutes.toString().padStart(2, '0') : displayMinutes}:{seconds.toString().padStart(2, '0')}</span>
        </span>
    );
};

const EpisodeStatus = ({status}) => {
    return (
        <div className={"episode-status-container " + status.toLowerCase()}>
            <div className="dot"></div>
            <span>{status}</span>
        </div>
    );
}

const EpisodeDownloads = ({downloads, status}) => {
    if (status.toLowerCase() != 'published') {
        return (
            <span className="episode-downloads not-published">
                N/A
            </span>
        )
    }
    return (
        <span className="episode-downloads">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M12.25 8.75V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H2.91667C2.60725 12.25 2.3105 12.1271 2.09171 11.9083C1.87292 11.6895 1.75 11.3928 1.75 11.0833V8.75" stroke="#4A5565" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.08398 5.83398L7.00065 8.75065L9.91732 5.83398" stroke="#4A5565" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7 8.75V1.75" stroke="#4A5565" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{downloads.toLocaleString()}</span>
        </span>
    );
}

function Home() {
    const navigation = useNavigate();
    const setContext = useContext(Context)[1];
    const access = useContext(AccessContext);
    useEffect(() => { setContext('Home') }, [setContext]);
    useEffect(() => { axios.get(process.env.REACT_APP_API_URL + 'validate') }, []);

    const [episodeCount, setEpisodeCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [recentEpisodes, setRecentEpisodes] = useState([]);

    const headings = [
        { key: 'id', name: 'ID', hide: true },
        { key: ['title', 'imageUrl'], name: 'Title', link: true, linkKey: 'id', Component: EpisodeTitle },
        { key: ['airDate'], name: 'Air Date', linkKey: 'id', Component: EpisodeAirDate },
        { key: ['duration'], center: true, name: 'Duration', linkKey: 'id', Component: EpisodeDuration },
        { key: ['downloads', 'status'], center: true, name: 'Downloads', linkKey: 'id', Component: EpisodeDownloads },
        { key: ['status'], center: true, name: 'Status', linkKey: 'id', Component: EpisodeStatus },
    ]
    
    const STATUS_COLORS = {
        'Completed': '#488F5A',
        'Processing': '#007EA7',
        'Shipped': '#007EA7',
        'Pending Payment': '#c68017',
        'On Hold': '#c68017',
        'Cancelled': '#6B7280',
        'Refunded': '#6B7280',
        'Returned': '#8B5CF6',
        'Failed': '#BE2839',
    };
    const getPath = (x, y, width, height, radius) => {
        return `M${x},${y + height}
                L${x},${y + radius}
                Q${x},${y} ${x + radius},${y}
                L${x + width - radius},${y}
                Q${x + width},${y} ${x + width},${y + radius}
                L${x + width},${y + height}
                Z`;
    };
    const CustomizedBar = (props) => {
        const { fill, x, y, width, height, payload } = props;
        
        // Look up the color based on the status in the data
        const statusColor = STATUS_COLORS[payload.status] || '#9CA3AF';

        const radius = 3; // Adjust for more or less roundness

        return (
            <path 
                d={getPath(x, y, width, height, radius)} 
                stroke="none" 
                fill={statusColor} 
            />
        );
    };

    const [listenData, setListenData] = useState([]);
    const [revenueData, setRevenueData] = useState([]);
    const [orderStatusData, setOrderStatusData] = useState([]);
    const [revenueYTD, setRevenueYTD] = useState(0);
    const [followers, setFollowers] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalPayouts, setTotalPayouts] = useState(0);
    const [todaysDownloads, setTodaysDownloads] = useState(0);
    const [yesterdaysDownloads, setYesterdaysDownloads] = useState(0);
    const [thisMonthsDownloads, setThisMonthsDownloads] = useState(0);
    const [lastMonthsDownloads, setLastMonthsDownloads] = useState(0);
    const defaultStatuses = [
        { status: 'Pending Payment', count: 0 },
        { status: 'Processing', count: 0 },
        { status: 'Shipped', count: 0 },
        { status: 'Completed', count: 0 },
        { status: 'On Hold', count: 0 },
        { status: 'Cancelled', count: 0 },
        { status: 'Refunded', count: 0 },
        { status: 'Returned', count: 0 },
        { status: 'Failed', count: 0 },
    ]

    useEffect(() => {
        if (!access.role)
            return;
        const url = access.role == 'Host/Guest' ? 'analytics/speaker/get' : 'analytics/podcast/get'
        axios.post(process.env.REACT_APP_API_URL + url)
            .then(r => {
                if (r?.data?.status === 'Success') {
                    setEpisodeCount(r.data.results.episodeCount || 0);
                    setRevenueYTD(r.data.results.ytdRevenue || 0);
                    setRevenueData(r.data.results.monthlyRevenue.map(m => ({month: m.monthDisplay.substr(0, 3).toUpperCase(), amount: m.monthlyTotal})));

                    const apiStatuses = r.data.results.orderStatuses;
                    const statusMap = new Map(apiStatuses.map(item => [item.status, item.count]));
                    const mergedData = defaultStatuses.map(def => ({
                        status: def.status,
                        count: statusMap.has(def.status) ? statusMap.get(def.status) : def.count
                    }));

                    setOrderStatusData(mergedData);
                    setTotalRevenue(r.data.results.totalRevenue || 0);
                    setTotalPayouts(r.data.results.totalPayouts || 0);

                    if (access.role != 'Host/Guest') {
                        setListenData(r.data.results.monthlyListens.map(m => ({month: m.monthDisplay.substr(0, 3).toUpperCase(), listens: m.monthlyListens})));
                        setFollowers(r.data.results.followers || 0);
                        setTodaysDownloads(r.data.results.todaysDownloads || 0);
                        setYesterdaysDownloads(r.data.results.yesterdaysDownloads || 0);
                        setThisMonthsDownloads(r.data.results.thisMonthsDownloads || 0);
                        setLastMonthsDownloads(r.data.results.lastMonthsDownloads || 0);

                        setTodaysDownloads(((Math.random() * 100) | 0) + 250);
                        setYesterdaysDownloads(((Math.random() * 100) | 0) + 200);
                        setThisMonthsDownloads(((Math.random() * 5000) | 0) + 10000);
                        setLastMonthsDownloads(((Math.random() * 5000) | 0) + 9000);
                        setFollowers(((Math.random() * 15000) | 0) + 10000);
                        var now = new Date();
                        setRecentEpisodes(r.data.results.recentEpisodes.map(r => {
                            r.status = !r.airDate ? 'Draft' : ((new Date(r.airDate)) > now ? 'Scheduled' : 'Published');
                            if (!r.imageUrl)
                                r.imageUrl = access.podcastImage;
                            r.downloads = ((Math.random() * 3500) | 0) + 50;

                            return r;
                        }) || []);
                    }
                }
            }).finally(() => setIsLoading(false));
    }, [access]);

    if (!access?.role || isLoading) {
        return (
            <div className="loading mt-100" style={{ color: '#AAA5BF' }}>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
            </div>
        )
    }
    return (
        <main>
            {access.role != 'Host/Guest' && (
                <div className="container mw-750 flex gap-20 w-100 f-wrap a-stretch">
                    <h1 className="page-title" style={{fontSize: '1.125rem'}}>Podcast Overview</h1>
                    <div className="grid gap-20 grid-dashboard-boxes">
                        <div className="content-box">
                            <div className="content-body dashboard-box">
                                <h2 className="dashboard-box-title w-100">Total Episodes</h2>
                                <p className="dashboard-box-value w-100">{episodeCount.toLocaleString()}</p>
                                <p className="dashboard-box-subtitle w-100"></p>
                            </div>
                        </div>
                        <div className="content-box">
                            <div className="content-body dashboard-box">
                                <h2 className="dashboard-box-title w-100">Today's Downloads</h2>
                                <p className="dashboard-box-value w-100 flex a-center" style={{ gap: 3 }}>{todaysDownloads.toLocaleString()} {todaysDownloads != yesterdaysDownloads && <ArrowIcon up={todaysDownloads > yesterdaysDownloads} />}</p>
                                <p className="dashboard-box-subtitle w-100"><PercentChange newValue={todaysDownloads} oldValue={yesterdaysDownloads} /></p>
                            </div>
                        </div>
                        <div className="content-box">
                            <div className="content-body dashboard-box">
                                <h2 className="dashboard-box-title w-100">Monthly Downloads</h2>
                                <p className="dashboard-box-value w-100 flex a-center" style={{ gap: 3 }}>{thisMonthsDownloads.toLocaleString()} {thisMonthsDownloads != lastMonthsDownloads && <ArrowIcon up={thisMonthsDownloads > lastMonthsDownloads} />}</p>
                                <p className="dashboard-box-subtitle w-100"><PercentChange newValue={thisMonthsDownloads} oldValue={lastMonthsDownloads} /></p>
                            </div>
                        </div>
                        <div className="content-box">
                            <div className="content-body dashboard-box">
                                <h2 className="dashboard-box-title w-100">Followers</h2>
                                <p className="dashboard-box-value w-100">{followers.toLocaleString()}</p>
                                <p className="dashboard-box-subtitle w-100"></p>
                            </div>
                        </div>
                    </div>
                    <List list={recentEpisodes} href="episode" checkBoxes={false} headings={headings} loading={false} onCheck={() => {}}
                        gridTemplateColumns="1fr max-content max-content max-content max-content max-content" CustomHeader={() => {
                            return (
                                <div className="grid-dashboard-header">
                                    <div className="dashboard-header-title">Recent Episodes</div>
                                    {!access.isHosted && (
                                        <Link to="/episode" className="btn btn-small gap-5">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                <path d="M2.91602 7H11.0827" stroke="white" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                                                <path d="M7 2.91602V11.0827" stroke="white" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                            Add Episode
                                        </Link>
                                    )}
                                </div>
                            );
                        }} actions={[{
                            text: (e) => {
                                return 'Edit'
                            },
                            action: (e) => {
                                navigation('/episode', {state: {id: e.id}});
                            }
                        }]} />
                </div>
            )}
            {access.role == 'Host/Guest' && (
                <div className="container mw-750 flex gap-20 w-100 f-wrap a-stretch">
                    <div className={access.role == 'Host/Guest' ? "w-30 xs-w-100 flex-grow" : "w-50 xs-w-100"}>
                        <div className="chart-container">
                            <h2>Episodes</h2>
                            <div style={{ width: '100%' }}>
                                {episodeCount.toLocaleString()}
                            </div>
                        </div>
                    </div>
                    
                    {access.role != 'Host/Guest' && (
                        <div className="w-50 xs-w-100">
                            <div className="chart-container">
                                <h2>Followers</h2>
                                <div style={{ width: '100%' }}>
                                    {followers.toLocaleString()}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className={access.role == 'Host/Guest' ? "w-30 xs-w-100 flex-grow" : "w-50 xs-w-100"}>
                        <div className="chart-container">
                            <h2>Revenue (YTD)</h2>
                            <div style={{ width: '100%' }}>
                                ${(revenueYTD || 0).toLocaleString()}
                            </div>
                        </div>
                    </div>
                    
                    <div className={access.role == 'Host/Guest' ? "w-30 xs-w-100 flex-grow" : "w-50 xs-w-100"}>
                        <div className="chart-container">
                            <h2>Funds Available</h2>
                            <div style={{ width: '100%' }}>
                                ${(totalRevenue - totalPayouts).toLocaleString()}
                            </div>
                        </div>
                    </div>

                    {access.role != 'Host/Guest' && (
                        <div className="w-50 xs-w-100">
                            <div className="chart-container">
                                <h2>Listens (Monthly)</h2>
                                <div style={{ width: '100%', height: 300 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={listenData}>
                                            {/* Adds the grid lines */}
                                            <CartesianGrid strokeDasharray="3 3" />
                                            
                                            {/* XAxis maps to the "name" key in our data */}
                                            <XAxis 
                                                dataKey="month" 
                                                type="category"
                                                interval={1}
                                                boundaryGap={true}
                                                tick={{ dx: -10, dy: 5, fontSize: 12 }}
                                            />
                                            <YAxis width={40} />
                                            
                                            {/* Tooltip shows data on hover */}
                                            <Tooltip />
                                            
                                            {/* The actual line. dataKey matches the object key "sales" */}
                                            <Line type="monotone" dataKey="listens" stroke="#8884d8" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className={access.role == 'Host/Guest' ? "w-100" : "w-50 xs-w-100"}>
                        <div className="chart-container">
                            <h2>Revenue (Monthly)</h2>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <LineChart data={revenueData}>
                                        {/* Adds the grid lines */}
                                        <CartesianGrid strokeDasharray="3 3" />
                                        
                                        {/* XAxis maps to the "name" key in our data */}
                                        <XAxis 
                                            dataKey="month" 
                                            type="category"
                                            interval={1}
                                            boundaryGap={true}
                                            tick={{ dx: -10, dy: 5, fontSize: 12 }}
                                        />
                                        <YAxis width={60} tickFormatter={(value) => 
                                            new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                maximumFractionDigits: 0, // Removes .00 to save space
                                            }).format(value)
                                        } />
                                        
                                        {/* Tooltip shows data on hover */}
                                        <Tooltip />
                                        
                                        {/* The actual line. dataKey matches the object key "sales" */}
                                        <Line type="monotone" dataKey="amount" stroke="#8884d8" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    
                    <div className="w-100">
                        <div className="chart-container">
                            <h2>Order Statuses</h2>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <BarChart data={orderStatusData}>
                                        {/* Adds the grid lines */}
                                        <CartesianGrid strokeDasharray="3 3" />
                                        
                                        {/* XAxis maps to the "name" key in our data */}
                                        <XAxis 
                                            dataKey="status" 
                                            type="category"
                                            boundaryGap={true}
                                            tick={{ dx: 0, dy: 5, fontSize: 12 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis width={40} axisLine={false} tickLine={false} />
                                        
                                        {/* Tooltip shows data on hover */}
                                        <Tooltip />
                                        
                                        {/* The actual line. dataKey matches the object key "sales" */}
                                        <Bar 
                                            dataKey="count" 
                                            shape={<CustomizedBar />}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

export default Home;